import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ScrollText, CheckCircle2, BookOpenCheck, RefreshCcw } from "lucide-react";

function PolicyToc({ sections }) {
  return (
    <nav className="lg:sticky lg:top-24 space-y-4" data-testid="policies-toc">
      <div className="label-eyebrow">▮ CONTENTS</div>
      {sections.map((s, i) => (
        <div key={s.id}>
          <a href={`#section-${s.id}`} className="font-display text-sm hover:text-[var(--ff-red)] transition-colors">
            {String(i + 1).padStart(2, "0")} · {s.title}
          </a>
          <ul className="mt-1 ml-6 space-y-1">
            {s.rules.map((r) => (
              <li key={r.id}><a href={`#rule-${r.id}`} className="text-xs text-[var(--ff-text-dim)] hover:text-white">{r.title}</a></li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function RuleCard({ rule, index, showAck, ackedVersion, onAck }) {
  const read = ackedVersion >= rule.version;
  const updated = !read && ackedVersion > 0;
  return (
    <article id={`rule-${rule.id}`} className="surface-card p-6 scroll-mt-24" data-testid={`policy-rule-${rule.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="font-display text-xl leading-tight">
          <span className="text-[var(--ff-red)] mr-2 font-mono-ui text-sm">{index}</span>{rule.title}
        </h3>
        {showAck && (read ? (
          <span className="inline-flex items-center gap-1 text-emerald-400 font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] shrink-0" data-testid={`policy-read-${rule.id}`}>
            <CheckCircle2 size={12} /> Read
          </span>
        ) : (
          <button onClick={() => onAck(rule.id)} className="btn-ghost inline-flex items-center gap-1.5 text-[0.65rem] !px-3 !py-1.5 shrink-0" data-testid={`policy-ack-${rule.id}`}>
            <BookOpenCheck size={12} /> {updated ? "Updated — Mark Read" : "Mark as Read"}
          </button>
        ))}
      </div>
      <div className="text-sm text-[var(--ff-text-dim)] leading-relaxed whitespace-pre-wrap">{rule.body}</div>
      <div className="mt-4 font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]/70">
        v{rule.version} · Updated {new Date(rule.updated_at).toLocaleDateString()}
      </div>
    </article>
  );
}

export default function Policies() {
  const { user } = useAuth();
  const loc = useLocation();
  const [data, setData] = useState(null);
  const [acked, setAcked] = useState({});
  const [isStaff, setIsStaff] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/policies");
    setData(d);
    if (!user) return;
    try {
      const { data: me } = await api.get("/policies/me");
      setAcked(me.acked || {});
      setIsStaff(me.is_staff);
      const ids = d.sections.flatMap((s) => s.rules.map((r) => r.id));
      if (me.is_staff && ids.length) api.post("/policies/notices/dismiss", { rule_ids: ids }).catch(() => {});
    } catch { /* signed-out view */ }
  }, [user]);

  useEffect(() => { load().catch(() => toast.error("Couldn't load policies")); }, [load]);

  useEffect(() => {
    if (!data || !loc.hash) return;
    const el = document.querySelector(loc.hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [data, loc.hash]);

  const ack = async (ruleId) => {
    try {
      await api.post("/policies/ack", { rule_ids: [ruleId] });
      const rule = data.sections.flatMap((s) => s.rules).find((r) => r.id === ruleId);
      setAcked((a) => ({ ...a, [ruleId]: rule.version }));
      toast.success("Marked as read");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sections = data?.sections || [];
  const total = sections.reduce((n, s) => n + s.rules.length, 0);
  const readCount = sections.flatMap((s) => s.rules).filter((r) => (acked[r.id] || 0) >= r.version).length;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="policies-page">
      <header className="mb-14 pb-10 border-b border-[var(--ff-border)] max-w-3xl">
        <div className="label-eyebrow mb-3 text-[var(--ff-red)] flex items-center gap-2"><ScrollText size={12} /> ▮ COMPANY POLICIES</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-5">HOW WE<br />OPERATE.</h1>
        <p className="text-[var(--ff-text-dim)] text-base leading-relaxed">
          Freshy Fresh Productions runs on transparency. Every policy our hosts, staff and community follow is published here for anyone to read.
        </p>
        <div className="mt-5 flex flex-wrap gap-4 font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
          <span data-testid="policies-count">{sections.length} sections · {total} policies</span>
          {data?.updated_at && <span>Last updated {new Date(data.updated_at).toLocaleDateString()}</span>}
          {isStaff && <span className="text-white" data-testid="policies-read-progress">You&apos;ve read {readCount}/{total}</span>}
        </div>
      </header>

      {!data ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm flex items-center gap-2"><RefreshCcw size={12} className="animate-spin" /> Loading…</div>
      ) : sections.length === 0 ? (
        <div className="surface-card p-12 text-center text-[var(--ff-text-dim)]" data-testid="policies-empty">No policies have been published yet.</div>
      ) : (
        <div className="grid lg:grid-cols-[260px_1fr] gap-12">
          <PolicyToc sections={sections} />
          <div className="space-y-16">
            {sections.map((s, si) => (
              <section key={s.id} id={`section-${s.id}`} className="scroll-mt-24" data-testid={`policy-section-${s.id}`}>
                <div className="mb-6">
                  <div className="label-eyebrow text-[var(--ff-red)] mb-1">SECTION {String(si + 1).padStart(2, "0")}</div>
                  <h2 className="font-display text-3xl">{s.title}</h2>
                  {s.description && <p className="text-[var(--ff-text-dim)] text-sm mt-2 max-w-2xl">{s.description}</p>}
                </div>
                <div className="space-y-4">
                  {s.rules.map((r, ri) => (
                    <RuleCard key={r.id} rule={r} index={`${si + 1}.${ri + 1}`} showAck={isStaff} ackedVersion={acked[r.id] || 0} onAck={ack} />
                  ))}
                  {s.rules.length === 0 && <div className="text-xs text-[var(--ff-text-dim)]">No rules in this section yet.</div>}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
